import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Camera, Check, Frame, Loader2, Table, X } from 'lucide-react';
import '@google/model-viewer';
import { showAlert } from '../../../components/HelperAlerts/HelpersAlerts.jsx';
import './ARViewer.css';

/**
 * Capa AR agnóstica del producto.
 *
 * No implementa AR a mano: delega en `<model-viewer>`, que enruta cada
 * plataforma a su visor nativo — Scene Viewer en Android, AR Quick Look en
 * iPhone (de ahí el USDZ) y WebXR donde exista. Escribir esto a mano dejaba
 * fuera a todo iOS, porque Safari no tiene WebXR.
 *
 * Los modelos se piden por URL firmada: los visores del sistema son procesos
 * externos y no pueden mandar la cabecera de sesión. Ver `ar_access.py`.
 *
 * Dos presentaciones:
 * - `direct` (lista del perfil y editor 3D): un modal para elegir el plano y
 *   confirmar, y de ahí al visor AR del sistema.
 * - por defecto: abre el visor 3D a pantalla completa con su botón de AR
 *   dentro. Queda como salida cuando el salto directo no es posible.
 */
const PLACEMENTS = [
    { key: 'floor', label: 'En una mesa', Icon: Table },
    { key: 'wall', label: 'En la pared', Icon: Frame },
];

/**
 * Corrección del plano, no un paso obligatorio: llega ya marcado el que
 * corresponde al producto. Cambiarlo relanza el visor, porque el plano no es
 * algo que los visores nativos acepten cambiar en caliente.
 */
function PlacementPicker({ value, onChange, available, compact = false }) {
    return (
        <div
            className={`ar-placement ${compact ? 'ar-placement--compact' : ''}`}
            role="group"
            aria-label="Dónde colocar la pieza"
        >
            {PLACEMENTS.map(({ key, label, Icon }) => (
                <button
                    key={key}
                    type="button"
                    className={`ar-placement__option ${value === key ? 'is-active' : ''}`}
                    onClick={() => onChange(key)}
                    disabled={!available?.[key]?.glb}
                    aria-pressed={value === key}
                >
                    <Icon size={compact ? 16 : 22} />
                    {label}
                    {!compact && (
                        <span className="ar-placement__mark" aria-hidden="true">
                            {value === key ? <Check size={14} /> : null}
                        </span>
                    )}
                </button>
            ))}
        </div>
    );
}

// iPadOS se anuncia como Mac; lo delata la pantalla táctil.
function isIOS() {
    const { userAgent, maxTouchPoints } = window.navigator;
    return /iPad|iPhone|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1);
}

PlacementPicker.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    available: PropTypes.object,
    compact: PropTypes.bool,
};

export default function ARViewer({ modelId, revision = 0, compact = false, inline = false, direct = false }) {
    const [links, setLinks] = useState(null);
    const [mode, setMode] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [arUnavailable, setArUnavailable] = useState(false);
    // Dónde se apoya la pieza. Lo propone el backend a partir del producto —
    // una caja de luz se cuelga, un llavero se deja encima de algo — así que
    // el caso normal no obliga a elegir nada.
    const [placement, setPlacement] = useState('floor');
    // El modelo ya está descargado y se puede entrar en AR. Hasta entonces el
    // botón del modal espera, porque `activateAR()` sobre un visor a medio
    // cargar no abre nada.
    const [viewerReady, setViewerReady] = useState(false);
    const viewerRef = useRef(null);
    const historyEntryRef = useRef(false);
    const isMountedRef = useRef(true);

    useEffect(() => () => { isMountedRef.current = false; }, []);

    const close = useCallback(({ fromHistory = false } = {}) => {
        setLinks(null);
        setMode(null);
        setMessage('');
        setArUnavailable(false);
        setViewerReady(false);
        if (historyEntryRef.current && !fromHistory) {
            historyEntryRef.current = false;
            window.history.back();
        }
    }, []);

    const pushHistoryEntry = () => {
        if (historyEntryRef.current) return;
        window.history.pushState({ ...window.history.state, customiz3dAr: true }, '', window.location.href);
        historyEntryRef.current = true;
    };

    // Segundo paso del flujo directo: el usuario ya eligió plano y pulsa. Ese
    // toque es el gesto que `activateAR()` necesita — lanzarlo solo al cargar
    // gastaba el gesto original en el await y el navegador podía bloquearlo.
    const launchAr = async () => {
        const viewer = viewerRef.current;
        if (!viewer) return;
        try {
            await viewer.activateAR();
        } catch (error) {
            console.warn('No se pudo entrar en AR:', error);
            pushHistoryEntry();
            setMode('overlay');
        }
    };

    const open = async () => {
        if (!modelId || !revision) {
            setMessage('Espera a que termine de generarse el modelo 3D.');
            return;
        }
        setLoading(true);
        setMessage('');
        try {
            // Los enlaces se piden al abrir, no al montar: caducan en minutos,
            // así que uno emitido al cargar la página llegaría muerto.
            const { data } = await axios.get(`/customized-keyrings/${modelId}/ar-links/`);
            if (!isMountedRef.current) return;
            setLinks(data);
            setPlacement(data.placement === 'wall' ? 'wall' : 'floor');
            setMode(direct ? 'direct' : 'overlay');
            if (!direct) pushHistoryEntry();
        } catch (error) {
            console.warn('No se pudieron obtener los enlaces AR:', error);
            if (isMountedRef.current) {
                setMessage('No hemos podido preparar el modelo para AR. Inténtalo de nuevo.');
            }
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    };

    // En modo directo se intenta entrar en AR en cuanto el modelo está listo.
    // Puede fallar: `activateAR()` quiere un gesto del usuario y el nuestro se
    // gastó antes del await. Si no cuela, se cae al visor a pantalla completa,
    // donde el botón de AR sí nace de un gesto limpio.
    useEffect(() => {
        if (mode !== 'direct') return undefined;
        const viewer = viewerRef.current;
        if (!viewer) return undefined;

        let cancelled = false;
        let sessionStarted = false;

        const fallBackToOverlay = () => {
            if (cancelled || !isMountedRef.current) return;
            pushHistoryEntry();
            setMode('overlay');
        };

        // `activateAR()` resuelve en cuanto la sesión arranca, no cuando
        // termina. Cerrar ahí desmontaba el <model-viewer>, y con él la propia
        // sesión WebXR, que vive dentro de ese elemento: pantalla negra y
        // cierre inmediato. El final real lo anuncia `ar-status`.
        const handleArStatus = (event) => {
            const status = event.detail?.status;
            if (status === 'session-started' || status === 'object-placed') {
                sessionStarted = true;
                return;
            }
            if (status === 'failed') {
                console.warn('El visor AR no pudo iniciarse.');
                setMessage('El visor AR no ha podido abrirse en este dispositivo.');
                fallBackToOverlay();
                return;
            }
            if (status === 'not-presenting' && sessionStarted) {
                if (!cancelled && isMountedRef.current) close();
            }
        };

        const handleLoad = () => {
            if (cancelled) return;
            if (!viewer.canActivateAR) {
                // Sin USDZ, un iPhone llega aquí; un Android no. Por eso el
                // aviso se decide con `canActivateAR` y no con que falte el
                // fichero: en Android el AR funciona igual y el mensaje sobra.
                if (isIOS() && !links?.[placement]?.usdz) {
                    setArUnavailable(true);
                    fallBackToOverlay();
                    return;
                }
                // Sin visor AR (un PC): caer al visor a pantalla completa solo
                // repetía el 3D que ya se ve. Se cierra y se explica.
                close();
                showAlert({
                    title: 'Verlo en tu espacio',
                    text: 'Este dispositivo no tiene realidad aumentada. Abre el diseño desde tu móvil para verlo a tamaño real en tu espacio.',
                    icon: 'info',
                });
                return;
            }
            // Listo, pero no se entra en AR aquí: lo dispara el botón del
            // modal. Así da tiempo a elegir el plano, y de paso `activateAR()`
            // nace de un toque de verdad, que es lo que exige el navegador.
            setViewerReady(true);
        };

        setViewerReady(false);
        viewer.addEventListener('load', handleLoad);
        viewer.addEventListener('ar-status', handleArStatus);
        viewer.addEventListener('error', fallBackToOverlay);
        return () => {
            cancelled = true;
            viewer.removeEventListener('load', handleLoad);
            viewer.removeEventListener('ar-status', handleArStatus);
            viewer.removeEventListener('error', fallBackToOverlay);
        };
        // `placement` entra en las dependencias a propósito: cambiar de plano
        // recarga el modelo, y ese cambio nace de un toque del usuario, que es
        // justo el gesto que `activateAR()` necesita.
    }, [mode, close, placement, links]);

    useEffect(() => {
        const handleBack = () => {
            if (!historyEntryRef.current) return;
            historyEntryRef.current = false;
            close({ fromHistory: true });
        };
        window.addEventListener('popstate', handleBack);
        return () => window.removeEventListener('popstate', handleBack);
    }, [close]);

    const busy = loading || mode === 'direct';
    const variant = links?.[placement] || {};

    const viewerProps = {
        ref: viewerRef,
        src: variant.glb,
        ...(variant.usdz ? { 'ios-src': variant.usdz } : {}),
        alt: 'Modelo 3D personalizado',
        ar: '',
        // Scene Viewer antes que WebXR a propósito. El WebXR de model-viewer
        // solo traslada si el dedo cae encima del modelo (`box.getHit`), y con
        // una pieza de 5 cm a tamaño real se falla casi siempre: cada fallo se
        // interpreta como giro. Scene Viewer coloca con retículo a pantalla
        // completa y arrastra desde cualquier punto.
        'ar-modes': 'scene-viewer webxr quick-look',
        // WebXR y Scene Viewer leen esto; Quick Look no, y por eso el USDZ de
        // pared trae el anclaje escrito dentro.
        'ar-placement': placement,
        // El modelo ya viene a escala real desde el backend. Sin esto los
        // visores dejan al usuario redimensionarlo y se pierde justo lo que
        // queremos enseñar, que es cuánto mide la pieza de verdad.
        'ar-scale': 'fixed',
        'shadow-intensity': '1',
        'environment-image': 'neutral',
        exposure: '1',
        loading: 'eager',
    };

    return (
        <>
            <div
                className={`ar-launcher ${compact ? 'ar-launcher--compact' : ''} ${inline ? 'ar-launcher--inline' : ''}`}
                aria-live="polite"
            >
                <button
                    type="button"
                    className="ar-launcher__button"
                    onClick={open}
                    disabled={!modelId || busy}
                >
                    {busy ? <Loader2 size={17} className="ar-spin" /> : <Camera size={17} />}
                    {busy ? 'Preparando…' : 'Ver en tu espacio'}
                </button>
                {message && <p className="ar-launcher__message">{message}</p>}
            </div>

            {/* El visor va a pantalla completa aunque no se vea: en WebXR la
                sesión se presenta dentro de este mismo elemento, así que tiene
                que tener caja real. Encima, sólo una pantalla de carga. */}
            {/* Por un portal a `document.body`: el lanzador vive dentro de una
                tarjeta, y basta con que cualquier ancestro cree un contexto de
                apilamiento (un `transform`, un `filter`, una opacidad) para que
                un `position: fixed` quede atrapado dentro de ella y las
                tarjetas siguientes lo tapen. Mismo motivo que en
                `ActionsDropdown`. */}
            {mode === 'direct' && createPortal(
                <div className="ar-preparing">
                    <model-viewer {...viewerProps} className="ar-preparing__viewer" />
                    <div
                        className="ar-preparing__backdrop"
                        onClick={(event) => { if (event.target === event.currentTarget) close(); }}
                    >
                        <div className="ar-preparing__modal" role="dialog" aria-modal="true">
                            <p className="ar-preparing__title">Verlo en tu espacio</p>
                            <p className="ar-preparing__hint">
                                Elige dónde quieres colocarlo y se abrirá el visor de
                                realidad aumentada de tu móvil.
                            </p>
                            <p className="ar-preparing__label">¿Dónde quieres colocarlo?</p>
                            <PlacementPicker value={placement} onChange={setPlacement} available={links} />
                            <button
                                type="button"
                                className="ar-preparing__go"
                                onClick={launchAr}
                                disabled={!viewerReady}
                            >
                                {viewerReady
                                    ? <><Camera size={18} /> Abrir en mi espacio</>
                                    : <><Loader2 size={18} className="ar-spin" /> Preparando modelo…</>}
                            </button>
                            <button type="button" className="ar-preparing__cancel" onClick={() => close()}>
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>,
                document.body,
            )}

            {mode === 'overlay' && createPortal(
                <div className="ar-overlay" role="dialog" aria-label="Vista en realidad aumentada">
                    <model-viewer {...viewerProps} camera-controls="" touch-action="pan-y" className="ar-overlay__viewer">
                        <button slot="ar-button" type="button" className="ar-overlay__cta">
                            <Camera size={18} />
                            Ver en tu espacio
                        </button>
                        <div slot="poster" className="ar-overlay__poster">
                            <Loader2 size={22} className="ar-spin" />
                            Cargando modelo…
                        </div>
                    </model-viewer>

                    <div className="ar-overlay__placement">
                        <PlacementPicker value={placement} onChange={setPlacement} available={links} compact />
                    </div>

                    <button
                        type="button"
                        className="ar-overlay__close"
                        onClick={() => close()}
                        aria-label="Cerrar vista 3D"
                    >
                        <X size={18} />
                    </button>

                    {arUnavailable && (
                        <div className="ar-note" role="status">
                            <p className="ar-note__title">Todavía no se puede ver en tu espacio</p>
                            <p className="ar-note__text">
                                A este modelo le falta la versión para iPhone. Vuelve a generarlo
                                desde el editor y estará disponible.
                            </p>
                            <button
                                type="button"
                                className="ar-note__dismiss"
                                onClick={() => setArUnavailable(false)}
                            >
                                Entendido
                            </button>
                        </div>
                    )}
                </div>,
                document.body,
            )}
        </>
    );
}

ARViewer.propTypes = {
    modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    revision: PropTypes.number,
    compact: PropTypes.bool,
    inline: PropTypes.bool,
    direct: PropTypes.bool,
};
